import {signal, WritableSignal} from '@angular/core';
import {FieldType, IField, IFieldExt, RealFieldType} from '@core/api/data-access';
import {BE} from '@utils';

import {HiddenPlaceholder} from '../../constants';
import {getFieldType, getRealFieldType} from '../../logic';
import {fieldsIcons} from './data';
import {FieldSettingsModel} from './field-settings.model';

import BEProp = BE.BEProp;

export class FieldModel implements IFieldExt {
	@BEProp() title: string;
	@BEProp() index: number;

	@BEProp({
		transform: (value: any) => getRealFieldType(value)?.realType,
	})
	type: FieldType;
	realType: RealFieldType;

	@BEProp() page: number;

	@BEProp() width: number;
	@BEProp() height: number;
	@BEProp() top: number;
	@BEProp() left: number;

	@BEProp() description: string;
	@BEProp() value = '';

	@BEProp() visible = true;
	@BEProp() readonly = false;
	@BEProp() required = false;
	@BEProp() delete = false;

	/* --------------- Field Settings --------------- */
	@BEProp({
		transform: (value: any) => new FieldSettingsModel(value).toString(),
	})
	settings: string;

	public settings$: WritableSignal<FieldSettingsModel> = signal<FieldSettingsModel>(null);

	/* -------------------------------------------  Common Indicators ------------------------------------------- */
	isHidden: boolean;

	/* -------------------------------------------------- */
	constructor(init: Partial<FieldModel | IField | IFieldExt>) {
		Object.assign(this, init);
		this.settings$.set(new FieldSettingsModel(init?.settings));

		// if the realType is not set, but the type is set, then the realType is the type:
		if (!init['realType'] && typeof init.type === 'number') {
			this.realType = init.type as unknown as RealFieldType;
			this.type = getFieldType(this.realType, this);
		} else if (!init['realType'] && typeof init.type === 'string') {
			const realTypeData = getRealFieldType(init.type as unknown as FieldType);
			this.realType = realTypeData.realType;
			this.settings$.update(settings => {
				settings.SpecialType = realTypeData.spacialType;
				return settings;
			});
		}

		// Check if the field is hidden:
		this.isHidden = HiddenPlaceholder.isHiddenPlaceHolder(this.title);

		/* -------------  Default Required: ------------- */
		// Check if the field is required:
		this.visible = this.type !== FieldType.metadata;

		/* ---------------  Default Page: --------------- */
		// Set the page to 1 if the field is metadata:
		this.page = this.type === FieldType.metadata ? 1 : this.page;

		/* --------------- Default value: --------------- */
		// Set the default value for checkbox,	if the value is not set, otherwise set the value to empty string:
		this.value = this.value || (this.type === FieldType.checkBox ? 'Off' : '');
	}

	/* --------------------------------------------  STATIC METHODS: -------------------------------------------- */
	public static getFieldTypeLabel(type: FieldType): string {
		if (type) {
			const typeLabel: string = type.toLowerCase();
			return window.translate(`struct.toolbox.${typeLabel}`);
		}
		return '';
	}

	public static getFieldTypeIcon(type: FieldType): string {
		return fieldsIcons[type];
	}

	toJSON(): IField {
		const settings = this.settings$()?.toString() || this.settings;
		return {
			title: this.title,
			index: this.index,
			type: getRealFieldType(this.type)?.realType as unknown as FieldType,
			page: this.page,
			width: this.width,
			height: this.height,
			top: this.top,
			left: this.left,
			value: this.value,
			visible: this.visible,
			readonly: this.readonly,
			required: this.required,
			delete: this.delete,
			settings,
			description: settings,
		};
	}
}
